import type { RoomAssetLink } from './types';

const NUMBER_WORDS: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
};

const ABBREVIATIONS: Array<[RegExp, string]> = [
  [/\bbed\s?room\b/g, 'bedroom'],
  [/\bbdrm?\b/g, 'bedroom'],
  [/\bdbl\b/g, 'double'],
  [/\bking\s?size\b/g, 'king'],
  [/\bnon\s?smoking\b/g, 'nonsmoking'],
];

const VIEW_COMPOUND = /^(pool|sea|ocean|garden|city|mountain|river|lake|beach)(view|front|side)$/;

// Only 1 may drop, and not before a counted noun: '2 King' and '2 bedroom' are different rooms.
const REDUNDANT_ONE = /\b1 (king|queen|double|twin|single|sofa|bed)\b/g;

const STOP_WORDS = new Set(['room', 'the', 'a', 'an', 'with', 'and', 'or', 'of']);

export const SIMILARITY_THRESHOLD = 0.8;

export function normaliseRoomName(name: string): string {
  let value = name.toLowerCase();
  value = value.replace(/[&+]/g, ' and ');
  value = value.replace(/[^a-z0-9]+/g, ' ');
  for (const [pattern, replacement] of ABBREVIATIONS) {
    value = value.replace(pattern, replacement);
  }
  value = value.replace(/\bone\b/g, '1');
  value = value.replace(REDUNDANT_ONE, '$1');
  return value.trim();
}

function singular(token: string): string {
  return token.length > 3 && token.endsWith('s') && !token.endsWith('ss') ? token.slice(0, -1) : token;
}

export function roomNameTokens(name: string): Array<string> {
  return normaliseRoomName(name)
    .split(' ')
    .flatMap((token) => {
      const compound = VIEW_COMPOUND.exec(token);
      return compound ? [compound[1], compound[2]] : [token];
    })
    .map((token) => NUMBER_WORDS[token] ?? token)
    .map(singular)
    .filter((token) => token && !STOP_WORDS.has(token));
}

function countBy(tokens: Array<string>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const token of tokens) {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  return counts;
}

export function roomNameSimilarity(a: string, b: string): number {
  if (normaliseRoomName(a) === normaliseRoomName(b)) {
    return 1;
  }
  const left = roomNameTokens(a);
  const right = roomNameTokens(b);
  if (left.length === 0 && right.length === 0) {
    return 1;
  }
  if (left.length === 0 || right.length === 0) {
    return 0;
  }
  const leftCounts = countBy(left);
  const rightCounts = countBy(right);
  let shared = 0;
  for (const [token, count] of leftCounts) {
    shared += Math.min(count, rightCounts.get(token) ?? 0);
  }
  return shared / (left.length + right.length - shared);
}

export type RoomAssetMatch = {
  asset: RoomAssetLink;
  similarity: number;
};

export function findSimilarRoomAssets(
  name: string,
  assets: Array<RoomAssetLink>,
  threshold = SIMILARITY_THRESHOLD,
): Array<RoomAssetMatch> {
  const scored = assets.map((asset) => ({ asset, similarity: roomNameSimilarity(asset.name, name) }));
  return scored.filter((match) => match.similarity >= threshold).sort((a, b) => b.similarity - a.similarity);
}
